import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, Smartphone, Key, CheckCircle2, AlertCircle, Eye, EyeOff, Copy, RefreshCw, CheckCheck, Fingerprint } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import PasskeyManager from '@/components/PasskeyManager';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

export default function TwoFactorAuth() {
  const [user, setUser] = useState(null);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [setting, setSetting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [setupStep, setSetupStep] = useState(1); // 1: instructions, 2: scan QR, 3: verify
  const [hasPasskeys, setHasPasskeys] = useState(false);
  const { confirmDialogProps, openConfirm } = useConfirmDialog();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const currentUser = await api.auth.me();
      setUser(currentUser);
      setTwoFactorEnabled(currentUser.two_factor_enabled || false);
      
      // Check if user has passkeys
      const passkeysResponse = await fetch('/api/passkey/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (passkeysResponse.ok) {
        const passkeys = await passkeysResponse.json();
        setHasPasskeys(passkeys && passkeys.length > 0);
      }
      
      // If user has 2FA enabled but we don't have the secret displayed, 
      // we can't show it (it's stored encrypted on server)
      // So we just show the enabled state
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startSetup = async () => {
    setSetting(true);
    try {
      // Generate secret (32 characters base32)
      const generatedSecret = generateBase32Secret();
      setSecret(generatedSecret);

      // Create OTP Auth URL
      const appName = 'BenefitsPro';
      const otpUrl = `otpauth://totp/${appName}:${user.email}?secret=${generatedSecret}&issuer=${appName}`;

      // Generate QR code
      const qrDataUrl = await generateQRCodeDataURL(otpUrl);
      setQrCode(qrDataUrl);
      setSetupStep(2); // Move to scan step
    } catch (err) {
      console.error(err);
      toast.error('Failed to start setup');
    } finally {
      setSetting(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setSecretCopied(true);
    toast.success('Secret copied to clipboard');
    setTimeout(() => setSecretCopied(false), 2000);
  };

  const regenerateQR = async () => {
    await startSetup();
    toast.success('New QR code generated');
  };

  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a complete 6-digit code');
      return;
    }

    setSetting(true);
    try {
      // Check if user has passkeys - they can't use both
      const passkeysResponse = await fetch('/api/passkey/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (passkeysResponse.ok) {
        const passkeys = await passkeysResponse.json();
        if (passkeys && passkeys.length > 0) {
          toast.error('You cannot enable 2FA while using passkeys. Passkeys already provide strong authentication.');
          setSetting(false);
          return;
        }
      }

      // Send to backend to save
      const updatedUser = await api.auth.updateMe({
        two_factor_enabled: true,
        two_factor_secret: secret
      });
      
      console.log('2FA enabled, response:', updatedUser);
      
      // Update local state
      setTwoFactorEnabled(true);
      setQrCode('');
      setSecret('');
      setVerificationCode('');
      setSetupStep(1);
      setShowSecret(false);
      
      toast.success('🎉 Two-factor authentication enabled successfully!');
      
      // Reload user data to confirm
      await loadData();
    } catch (err) {
      console.error('Error enabling 2FA:', err);
      toast.error('Failed to enable 2FA. Please try again.');
    } finally {
      setSetting(false);
    }
  };

  const disable2FA = async () => {
    setSetting(true);
    try {
      await api.auth.updateMe({
        two_factor_enabled: false,
        two_factor_secret: null
      });
      setTwoFactorEnabled(false);
      setQrCode('');
      setSecret('');
      setVerificationCode('');
      setSetupStep(1);
      setShowSecret(false);
      toast.success('Two-factor authentication disabled');
    } catch (err) {
      console.error(err);
      toast.error('Failed to disable 2FA');
    } finally {
      setSetting(false);
    }
  };

  const confirmDisable2FA = () => {
    openConfirm({
      title: 'Disable two-factor authentication?',
      description: 'Disabling removes the extra verification step when signing in. Only continue if you have another secure way to access your account or recovery codes saved.',
      confirmLabel: 'Disable 2FA',
      isDestructive: true,
      onConfirm: disable2FA
    });
  };

  const cancelSetup = () => {
    setQrCode('');
    setSecret('');
    setVerificationCode('');
    setSetupStep(1);
    setShowSecret(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Account Security</h1>
        <p className="text-slate-600 mt-2">
          Protect your account with modern security methods
        </p>
      </div>

      <Tabs defaultValue="passkey" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="passkey" className="flex items-center gap-2">
            <Fingerprint className="w-4 h-4" />
            Passkeys (Recommended)
          </TabsTrigger>
          <TabsTrigger value="2fa" className="flex items-center gap-2">
            <Smartphone className="w-4 h-4" />
            Authenticator App
          </TabsTrigger>
        </TabsList>

        <TabsContent value="passkey">
          <PasskeyManager />
        </TabsContent>

        <TabsContent value="2fa">
          {/* Passkey Blocker Message */}
          {hasPasskeys && !twoFactorEnabled && (
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300 mb-6">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Fingerprint className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-blue-900 mb-2">
                      Passkeys Are Enabled (Recommended)
                    </p>
                    <p className="text-sm text-blue-800 mb-3">
                      You're already using <strong>passkeys</strong> - a more secure and convenient authentication method than traditional 2FA. 
                      Passkeys use biometric authentication (Face ID, Touch ID, Windows Hello) and are phishing-resistant.
                    </p>
                    <div className="bg-white/60 rounded-lg p-3 border border-blue-200">
                      <p className="text-xs text-blue-900 font-semibold mb-1">Why we recommend passkeys:</p>
                      <ul className="text-xs text-blue-800 space-y-1">
                        <li>✓ <strong>Stronger security</strong> - Phishing-resistant and hardware-backed</li>
                        <li>✓ <strong>More convenient</strong> - No codes to type, just biometric verification</li>
                        <li>✓ <strong>Faster login</strong> - One touch or face scan instead of typing codes</li>
                      </ul>
                    </div>
                    <p className="text-xs text-blue-700 mt-3">
                      If you want to use authenticator app-based 2FA instead, you must first remove your passkeys from the Passkeys tab.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Status Card */}
          {!hasPasskeys && (
            <Card className={twoFactorEnabled ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-300 mb-6' : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 mb-6'}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${twoFactorEnabled ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                      {twoFactorEnabled ? (
                        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      ) : (
                        <AlertCircle className="w-6 h-6 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <p className={`text-lg font-semibold ${twoFactorEnabled ? 'text-emerald-900' : 'text-amber-900'}`}>
                        {twoFactorEnabled ? 'Two-Factor Authentication Active' : 'Two-Factor Authentication Inactive'}
                      </p>
                      <p className={`text-sm ${twoFactorEnabled ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {twoFactorEnabled 
                          ? 'Your account is protected with an extra layer of security' 
                          : 'Add an extra layer of security to protect your account'}
                      </p>
                    </div>
                  </div>
                  <Badge className={`${twoFactorEnabled ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'} text-white px-4 py-1 text-sm`}>
                    {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {!hasPasskeys && !twoFactorEnabled ? (
        <AnimatePresence mode="wait">
          <motion.div 
            key={setupStep}
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="shadow-lg">
              <CardHeader className="border-b bg-slate-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">Enable Two-Factor Authentication</CardTitle>
                      <CardDescription className="text-sm mt-1">
                        Step {setupStep} of 3
                      </CardDescription>
                    </div>
                  </div>
                  {setupStep > 1 && (
                    <Button variant="ghost" size="sm" onClick={cancelSetup}>
                      Cancel
                    </Button>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="p-6">
                {setupStep === 1 && (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800 font-medium mb-2">What is Two-Factor Authentication?</p>
                      <p className="text-sm text-blue-700">
                        2FA adds an extra layer of security by requiring both your password and a time-based code from your phone to log in.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-lg hover:border-emerald-300 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-emerald-700 font-semibold">1</span>
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900 mb-2">Download an Authenticator App on Your Phone</p>
                          <p className="text-sm text-slate-600 mb-3">
                            📱 <strong>Use your smartphone's app store</strong> to download one of these free authenticator apps:
                          </p>
                          <div className="space-y-2 ml-4">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-white font-semibold">Google Authenticator</Badge>
                              <span className="text-xs text-slate-500">(iOS/Android)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-white font-semibold">Microsoft Authenticator</Badge>
                              <span className="text-xs text-slate-500">(iOS/Android)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-white font-semibold">Authy</Badge>
                              <span className="text-xs text-slate-500">(iOS/Android/Desktop)</span>
                            </div>
                          </div>
                          <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mt-3 border border-amber-200">
                            ⚠️ Note: These apps must be installed on your mobile device, not on this computer
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-slate-700 font-semibold">2</span>
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900 mb-1">Scan QR Code</p>
                          <p className="text-sm text-slate-600">
                            We'll generate a unique QR code for you to scan with your authenticator app
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-slate-700 font-semibold">3</span>
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900 mb-1">Verify Setup</p>
                          <p className="text-sm text-slate-600">
                            Enter the code from your app to confirm everything works correctly
                          </p>
                        </div>
                      </div>
                    </div>

                    <Button 
                      onClick={startSetup}
                      disabled={setting}
                      className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-lg"
                    >
                      {setting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Setting up...
                        </>
                      ) : (
                        <>
                          <Smartphone className="w-5 h-5 mr-2" />
                          Start Setup
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {setupStep === 2 && qrCode && (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800 font-medium mb-1">📱 Open your authenticator app</p>
                      <p className="text-sm text-blue-700">
                        Scan the QR code below or manually enter the code
                      </p>
                    </div>

                    <div className="flex flex-col items-center space-y-6">
                      <div className="relative">
                        <div className="p-6 bg-white rounded-2xl shadow-xl border-2 border-slate-200">
                          <img 
                            src={qrCode} 
                            alt="QR Code for 2FA Setup" 
                            className="w-72 h-72 rounded-lg"
                          />
                        </div>
                        <Button
                          onClick={regenerateQR}
                          variant="outline"
                          size="sm"
                          className="absolute -bottom-3 right-4 shadow-md"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Regenerate
                        </Button>
                      </div>

                      <div className="w-full max-w-md">
                        <div className="border-t border-slate-200 pt-4 pb-2">
                          <p className="text-center text-sm text-slate-500 mb-3">Can't scan? Enter this code manually:</p>
                        </div>
                        
                        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1">
                              <Label className="text-xs text-slate-500 uppercase tracking-wide mb-2 block">
                                Secret Key
                              </Label>
                              <div className="font-mono text-base font-semibold text-slate-900 break-all">
                                {showSecret ? secret : '•'.repeat(32)}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => setShowSecret(!showSecret)}
                                variant="ghost"
                                size="sm"
                                className="h-9 w-9 p-0"
                              >
                                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </Button>
                              <Button
                                onClick={copySecret}
                                variant="ghost"
                                size="sm"
                                className="h-9 w-9 p-0"
                              >
                                {secretCopied ? <CheckCheck className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button 
                      onClick={() => setSetupStep(3)}
                      className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                    >
                      I've Scanned the Code
                      <Key className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}

                {setupStep === 3 && (
                  <div className="space-y-6">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <p className="text-sm text-emerald-800 font-medium mb-1">✅ Almost done!</p>
                      <p className="text-sm text-emerald-700">
                        Enter the 6-digit code from your authenticator app to verify the setup
                      </p>
                    </div>

                    <div className="space-y-3">
                      <Label className="text-base font-semibold">Enter Verification Code</Label>
                      <Input
                        type="text"
                        maxLength={6}
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="000000"
                        className="h-20 text-center text-5xl tracking-[0.5em] font-mono font-bold border-2 focus:border-emerald-500"
                        autoFocus
                      />
                      <p className="text-sm text-slate-500 text-center">
                        The code refreshes every 30 seconds
                      </p>
                    </div>

                    <div className="flex gap-3">
                      <Button 
                        onClick={() => setSetupStep(2)}
                        variant="outline"
                        className="flex-1 h-12"
                      >
                        Back to QR Code
                      </Button>
                      <Button 
                        onClick={verifyAndEnable}
                        disabled={setting || verificationCode.length !== 6}
                        className="flex-1 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                      >
                        {setting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Verifying...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-5 h-5 mr-2" />
                            Verify & Enable
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="shadow-lg">
            <CardHeader className="border-b bg-slate-50">
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-600" />
                Manage Two-Factor Authentication
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center gap-4 p-5 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-emerald-900 text-lg">Your Account is Protected</p>
                  <p className="text-sm text-emerald-700 mt-1">
                    Two-factor authentication is active and working. You'll need your authenticator app to sign in.
                  </p>
                </div>
              </div>

              <div className="border-t pt-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-800 font-medium mb-1">⚠️ Warning</p>
                  <p className="text-sm text-red-700">
                    Disabling 2FA will make your account less secure. Make sure you have another way to access your account.
                  </p>
                </div>

                <Button 
                  onClick={confirmDisable2FA}
                  disabled={setting}
                  variant="outline"
                  className="w-full h-11 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                >
                  {setting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin mr-2" />
                      Disabling...
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Disable Two-Factor Authentication
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
        </TabsContent>
      </Tabs>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}

// Helper: Generate base32 secret
function generateBase32Secret() {
  const base32chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let secret = '';
  for (let i = 0; i < 32; i++) {
    secret += base32chars[Math.floor(Math.random() * base32chars.length)];
  }
  return secret;
}

// Helper: Generate QR code using qrcode library
async function generateQRCodeDataURL(text) {
  try {
    const qrDataUrl = await QRCode.toDataURL(text, {
      width: 256,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#ffffff'
      }
    });
    return qrDataUrl;
  } catch (err) {
    console.error('Error generating QR code:', err);
    throw err;
  }
}

// Helper: Verify TOTP (simplified - in production, use speakeasy or similar)
function verifyTOTP(secret, token) {
  // This is a placeholder - in production, use proper TOTP verification
  // For demo purposes, accept any 6-digit code
  return token.length === 6;
}